/**
 * Hemispherical / fisheye footprint mode — shared seam for the coverage cone.
 *
 * A fisheye/hemispheric lens means different ground footprints depending on how
 * the camera is mounted:
 *   • CEILING (dome looking straight down): a 180° hemispheric lens projects a
 *     FULL 360° circle on the floor.
 *   • WALL / corner (lens facing out): the same 180° lens covers a forward
 *     half-disc (the legacy directional wedge).
 *
 * `Camera.fisheyeMode` records that choice; these helpers turn it into the
 * footprint geometry every surface (2D map, 3D, DXF, PDF) reads.
 *
 * IMPORTANT — footprint only. DORI / lens / pixel-density math must keep using
 * the REAL lens HFOV (`camera.hfov`), never `footprintHfovDeg` — a 180° lens is
 * still a 180° lens optically even when its floor footprint is a full circle.
 */
import type { Camera } from './types';

export type FisheyeMode = 'ceiling' | 'wall';

type FootprintCamera = Pick<Camera, 'hfov' | 'fisheyeMode'>;

/** True when the coverage footprint is a full 360° ground circle: a
 *  ceiling-mounted fisheye (fisheyeMode 'ceiling') OR an already-360° lens. */
export function isCircularFootprint(cam: FootprintCamera): boolean {
  return cam.fisheyeMode === 'ceiling' || cam.hfov >= 359.5;
}

/** HFOV (deg) to use when DRAWING the coverage cone/footprint. A ceiling
 *  fisheye draws a full 360° circle regardless of its narrower lens HFOV. */
export function footprintHfovDeg(cam: FootprintCamera): number {
  return isCircularFootprint(cam) ? 360 : cam.hfov;
}

/** First (wide-end) focal length in mm from a catalog string like "1.22mm" or
 *  "2.8-12mm". Null when unparseable. */
function parseMinFocalMm(focal?: string): number | null {
  if (!focal) return null;
  // Strip a multi-lens multiplier ("2x2.8mm" / "4×4mm" → "2.8mm"/"4mm") so a
  // dual/quad-lens panoramic STRIP isn't read as a 2mm fisheye lens.
  const cleaned = focal.replace(/\b\d+\s*[x×]\s*/i, '');
  const m = cleaned.match(/(\d+(?:\.\d+)?)/);
  return m ? parseFloat(m[1]) : null;
}

/** Sensor aspect ratio (w/h) from a "WxH" resolution string, else null.
 *  Exported for povProjection.ts: the ≥1.9 "stitched panoramic strip" veto is
 *  the single rule that keeps a HORIZONTAL 360° multi-imager (e.g. Dahua PSDW
 *  head0 at 8192×1800) from being read as a downward hemisphere, so the POV
 *  classifier must apply the identical test rather than re-derive it. */
export function sensorAspect(resolution?: string): number | null {
  const m = String(resolution ?? '').match(/^(\d+)\s*[x×]\s*(\d+)$/);
  if (!m) return null;
  const h = Number(m[2]);
  return h > 0 ? Number(m[1]) / h : null;
}

/** Standard frame shapes, so a readout can be named rather than printed as a
 *  bare decimal. Ordered widest first; the first within tolerance wins. */
const ASPECT_NAMES: ReadonlyArray<readonly [number, string]> = [
  [32 / 9, '32:9'],
  [21 / 9, '21:9'],
  [16 / 9, '16:9'],
  [16 / 10, '16:10'],
  [3 / 2, '3:2'],
  [4 / 3, '4:3'],
  [5 / 4, '5:4'],
  [1, '1:1'],
  [3 / 4, '3:4'],
  [9 / 16, '9:16'],
];

/**
 * Human name for a frame shape, e.g. 1.7536 -> "16:9".
 *
 * Tolerance is 1.5 percent because real sensors miss the nominal ratio by a
 * little and the name should not flip on that: 2960x1688 is 1.7536 and 2688x1520
 * is 1.7684, both of which an installer reads as 16:9. Anything outside every
 * band is printed as a ratio against 1 rather than forced into the nearest
 * name, since a genuinely odd readout is worth SEEING as odd.
 */
export function aspectLabel(aspect: number): string {
  if (!Number.isFinite(aspect) || aspect <= 0) return '';
  for (const [value, name] of ASPECT_NAMES) {
    if (Math.abs(aspect - value) / value < 0.015) return name;
  }
  return `${aspect.toFixed(2)}:1`;
}

/** Curated ceiling-fisheye SKUs the rules below can't infer from specs alone
 *  (verified single downward fisheyes whose catalog HFOV/focal is missing or
 *  mis-tagged). Keyed by exact catalog name. Populated from the fov-catalog
 *  research after independent verification — only confirmed full-circle SKUs. */
const CEILING_FISHEYE_NAMES: ReadonlySet<string> = new Set<string>([]);

/** Verified NON-circle SKUs the rules below would otherwise flag — a downward
 *  fisheye lens used in a context that does NOT project a full floor circle:
 *  corner-mount cameras (look outward across a room) and asymmetric modular
 *  fisheye sensors. These render as a directional wedge. Wins over every rule. */
const NEVER_CEILING_FISHEYE: ReadonlySet<string> = new Set<string>([
  'AXIS P9117-PV',                 // corner camera — outward hemisphere, not a floor circle
  'AXIS F2137-RE-FISHEYE-SENSOR',  // modular sensor, asymmetric 185°×125° FOV
]);

/** Heuristic: does a catalog model describe a CEILING fisheye / hemispheric
 *  camera whose accurate footprint is a full ground circle? Distinct from a
 *  wall-mounted "panoramic strip" (longer focal, wide-aspect sensor) which
 *  stays a directional wedge. Used to default `fisheyeMode` on placement; the
 *  user can always override via the camera panel toggle. */
export function isCeilingFisheyeModel(model: {
  hfov: number;
  type?: string;
  focalLength?: string;
  resolution?: string;
  name?: string;
}): boolean {
  if (model.name && NEVER_CEILING_FISHEYE.has(model.name)) return false;
  // Corner-mount cameras (even with a fisheye lens) look OUTWARD across a room
  // from a wall corner — a directional ~hemisphere, not a centred floor circle.
  if (/\bcorner\b/i.test(model.name ?? '')) return false;
  if (model.name && CEILING_FISHEYE_NAMES.has(model.name)) return true;
  if (model.type !== 'dome' && model.type !== 'panoramic') return false;
  const asp = sensorAspect(model.resolution);
  // Wide-aspect sensor (≥~2:1, e.g. 4320×1920) = a stitched "panoramic strip"
  // — short focal but NOT a downward fisheye. Render as a directional wedge.
  if (asp !== null && asp >= 1.9) return false;
  const focalMin = parseMinFocalMm(model.focalLength);
  // An ultra-short lens (≤1.9mm) is a fisheye regardless of the catalog's
  // (sometimes wrong) HFOV tag → full floor circle.
  if (focalMin !== null && focalMin <= 1.9) return true;
  if (model.hfov < 170) return false;
  // Hemispheric (≥170°): ≤2.2mm is a downward fisheye; a wall "panoramic strip"
  // lens is ≥2.3mm (e.g. Axis M4308 @2.3mm).
  if (focalMin !== null) return focalMin <= 2.2;
  // Absent/unparseable focal: a fisheye name, OR a square-ish sensor — a
  // downward fisheye image is ~1:1, a wall panoramic strip is wide (≥~2:1).
  if (/fish\s?eye|panomorph|hemispher/i.test(model.name ?? '')) return true;
  return asp !== null && asp <= 1.55;
}
